import React, {useEffect, useState, useRef} from 'react';
import dayjs from 'dayjs';
import minMax from 'dayjs/plugin/minMax';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import apiClient from '../../context/apiClient';
import {useAuth} from '../../context/AuthContext';
import EventCard from '../Plantafel/EventCard';
import MatchEventsButton from '../Plantafel/MatchEventsButton';
import Plus from '../../assets/icons/Plus.svg';
import Calendar from '../../assets/icons/Calendar.svg';
import NewEventForm from "../Plantafel/NewEventForm";
import Unlock from '../../assets/icons/Unlock.svg';
import Lock from '../../assets/icons/LockBold.svg';
import Trash from '../../assets/icons/Trash.svg';

dayjs.extend(minMax);
dayjs.extend(isSameOrBefore);
dayjs.extend(isBetween);

const EVENT_WIDTH = 320;
const COLUMN_GAP = 24;
const DAY_HEIGHT = 600;

const getDaySpan = (start, end) => {
    const startDay = dayjs(start).startOf('day');
    const endDay = dayjs(end).startOf('day');
    return Math.max(1, endDay.diff(startDay, 'day') + 1);
};

const shouldRenderOnDate = (event, date, earliestDate) => {
    const eventStart = dayjs(event.timeframe.startTime).startOf('day');
    const current    = dayjs(date).startOf('day');
    const firstVisibleDay = dayjs.max(eventStart, dayjs(earliestDate));
    return current.isSame(firstVisibleDay, 'day');
};


const getDateRange = (start, end) => {
    const days = [];
    let current = dayjs(start).startOf('day');
    while (current.isSameOrBefore(end, 'day')) {
        days.push(current.format('YYYY-MM-DD'));
        current = current.add(1, 'day');
    }
    return days;
};

const getSpanningEvents = (events, currentDate) => {
    const current = dayjs(currentDate).startOf('day');
    return events.filter(event => {
        const start = dayjs(event.timeframe.startTime).startOf('day');
        const end = dayjs(event.timeframe.endTime).endOf('day');
        return current.isBetween(start, end, null, '[]') && event.column != null;
    });
};

const positionEvents = (events, previousDayEvents, currentDate) => {
    const sorted = [...events].sort(
        (a, b) => new Date(a.timeframe.startTime) - new Date(b.timeframe.startTime)
    );

    // Events, die schon vom Vortag in diesen Tag hineinragen
    const activeFromYesterday = getSpanningEvents(previousDayEvents, currentDate);

    // IDs der bereits "getragenen" Events -> diese NICHT nochmal positionieren
    const carriedIds = new Set(activeFromYesterday.map(e => e.id));

    const positioned = [];

    sorted.forEach(event => {
        // Duplikate vermeiden: wenn schon als carried vorhanden, überspringen
        if (carriedIds.has(event.id)) return;

        let column = 0;
        while ([...positioned, ...activeFromYesterday].some(e => {
            const eventDays = getDateRange(event.timeframe.startTime, event.timeframe.endTime);
            const otherDays = getDateRange(e.timeframe.startTime, e.timeframe.endTime);
            return eventDays.includes(currentDate) && otherDays.includes(currentDate) && e.column === column;
        })) {
            column++;
        }
        positioned.push({ ...event, column });
    });

    return [...activeFromYesterday, ...positioned];
};


const Plantafel = () => {
    const {user} = useAuth();
    const scrollContainerRef = useRef(null);
    const loadingRef = useRef({});
    const [groupedEvents, setGroupedEvents] = useState({});
    const [allQualifications, setAllQualifications] = useState([]);
    const [lockMapPerEvent, setLockMapPerEvent] = useState({});
    const [showMatchPanel, setShowMatchPanel] = useState(false);
    const [showAddEventPanel, setShowAddEventPanel] = useState(false);
    const [selectedStartDate, setSelectedStartDate] = useState(dayjs().startOf('month').format('YYYY-MM-DD'));
    const [selectedEndDate, setSelectedEndDate] = useState(dayjs().endOf('month').format('YYYY-MM-DD'));
    const [isDeleting, setIsDeleting] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const deleteArmTimeoutRef = useRef(null);


    const handleLockChange = (eventId, rowKey, entry) => {
        setLockMapPerEvent(prev => ({
            ...prev,
            [eventId]: {
                ...(prev[eventId] || {}),
                [rowKey]: entry,
            },
        }));
    };

    // When the user changes the start date, if it would become after the current end date,
    // push the end date forward to the end of the same month as the new start.
    const handleStartChange = (e) => {
        const newStart = e.target.value;
        setSelectedStartDate(newStart);
        try {
            if (dayjs(newStart).isAfter(dayjs(selectedEndDate), 'day')) {
                const endOfMonth = dayjs(newStart).endOf('month').format('YYYY-MM-DD');
                setSelectedEndDate(endOfMonth);
            }
        } catch (err) {
            // In case of invalid dates, just set the start and don't modify end
            console.error('Invalid date while handling start change', err);
        }
    };

    const handleEventTimeframeChanged = (oldEvent) => {
        // Get all dates the event was previously shown
        const oldDates = getDateRange(oldEvent.timeframe.startTime, oldEvent.timeframe.endTime);
        const eventId = oldEvent.id;

        setGroupedEvents(prev => {
            const updated = {...prev};
            // Remove event from all old dates
            oldDates.forEach(date => {
                if (updated[date]) {
                    updated[date] = updated[date].filter(ev => ev.id !== eventId);
                    // If the date is now empty, you may optionally delete the key
                    if (updated[date].length === 0) delete updated[date];
                }
            });
            return updated;
        });

        // Re-fetch events for both old and new months
        void fetchEventsForRange(selectedStartDate, selectedEndDate);

    };

    const fetchEventsForRange = async (start, end) => {
        setGroupedEvents({});
        loadingRef.current = {};
        const res = await apiClient.get('/api/events/plantafel', {
            params: { start, end },
            withCredentials: true,
        });
        const data = res.data; // Array von Events

        // wie bisher: events pro Tag einsortieren
        const grouped = data.reduce((acc, event) => {
            getDateRange(event.timeframe.startTime, event.timeframe.endTime).forEach(date => {
                (acc[date] ||= []).push(event);
            });
            return acc;
        }, {});
        setGroupedEvents(grouped);
    };

    const earliestDate = dayjs(selectedStartDate).startOf('day');
    const latestDate = dayjs(selectedEndDate).startOf('day');
    const fullDayList = getDateRange(earliestDate, latestDate);


    useEffect(() => {
        if (!user) return;
        void fetchEventsForRange(selectedStartDate, selectedEndDate);
    }, [user, selectedStartDate, selectedEndDate]);


    useEffect(() => {
        apiClient.get('/api/utility/allQualifications')
            .then(res => setAllQualifications(res.data.map(q => q.name)))
            .catch(err => console.error("Fehler beim Laden der Qualifikationen:", err));
    }, []);

    const allLocked = React.useMemo(() => {
        const events = Object.values(lockMapPerEvent);
        if (events.length === 0) return false; // nothing yet → show "Lock all" as default action
        for (const rows of events) {
            for (const entry of Object.values(rows)) {
                if (!entry?.isLocked) return false;
            }
        }
        return true;
    }, [lockMapPerEvent]);


    const toggleAllLocks = () => {
        const nextLockState = !allLocked; // if not all locked → lock all, else unlock all
        setLockMapPerEvent(prev => {
            const updated = {};
            for (const [eventId, rows] of Object.entries(prev)) {
                updated[eventId] = Object.fromEntries(
                    Object.entries(rows).map(([rowKey, entry]) => [
                        rowKey,
                        { ...entry, isLocked: nextLockState }
                    ])
                );
            }
            return updated;
        });
    };

    const handleEventDeleted = (eventId) => {
        setGroupedEvents(prev => {
            const updated = {...prev};
            for (const date of Object.keys(updated)) {
                updated[date] = updated[date].filter(ev => ev.id !== eventId);
                if (updated[date].length === 0) delete updated[date];
            }
            return updated;
        });
    };

    const disarmDelete = React.useCallback(() => {
        setShowDeleteConfirm(false);
        if (deleteArmTimeoutRef.current) {
            clearTimeout(deleteArmTimeoutRef.current);
            deleteArmTimeoutRef.current = null;
        }
    }, []);

    useEffect(() => {
        // Auto-disarm if date range changes or while deleting
        disarmDelete();
    }, [selectedStartDate, selectedEndDate, isDeleting, disarmDelete]);

    useEffect(() => {
        // Cleanup on unmount
        return () => {
            if (deleteArmTimeoutRef.current) clearTimeout(deleteArmTimeoutRef.current);
        };
    }, []);

    const getVisibleEventIds = React.useCallback(() => {
        // unique ids across the currently selected date range
        const ids = new Set();
        fullDayList.forEach((date) => {
            (groupedEvents[date] || []).forEach((ev) => ids.add(ev.id));
        });
        return Array.from(ids);
    }, [groupedEvents, fullDayList]);

    // Only the events which are NOT locked should be deletable.
    const getUnlockedVisibleEventIds = React.useCallback(() => {
        const ids = getVisibleEventIds();
        return ids.filter(id => {
            // Find any instance of the event in groupedEvents to inspect its status
            let found = null;
            for (const date of Object.keys(groupedEvents)) {
                const ev = (groupedEvents[date] || []).find(e => e.id === id);
                if (ev) { found = ev; break; }
            }
            // If we can't find the event object, treat it as not deletable
            if (!found) return false;

            // Normalize status: prefer eventStatus, fall back to status (some APIs use different names)
            const rawStatus = (found.eventStatus || found.status || '').toString();
            const status = rawStatus.trim().toLowerCase();

            // Consider an event deletable only when its status is 'open' (or anything but 'closed')
            return status !== 'closed';
        });
    }, [getVisibleEventIds, groupedEvents]);

    const performDeleteVisibleEvents = async () => {
        const ids = getUnlockedVisibleEventIds();
        console.log("Deleting unlocked Ids:", ids);
        if (ids.length === 0) {
            alert('Keine offenen Events zum Löschen.');
            return;
        }

        try {
            setIsDeleting(true);
            await apiClient.delete('/api/events/deleteMultipleEvents', {
                data: ids,
                withCredentials: true,
            });
            await fetchEventsForRange(selectedStartDate, selectedEndDate);
        } catch (err) {
            console.error('Fehler beim Löschen mehrerer Events:', err);
            alert('Löschen fehlgeschlagen. Bitte erneut versuchen.');
        } finally {
            setIsDeleting(false);
            disarmDelete();
        }
    };

    const handleDeleteClick = () => {
        const ids = getVisibleEventIds();
        if (ids.length === 0 || isDeleting) return;

        // Open a confirmation modal instead of the previous "armed" UX
        setShowDeleteConfirm(true);
    };

    // Compute once per render (keeps JSX clean)
    const unlockedVisibleIds = React.useMemo(() => getUnlockedVisibleEventIds(), [getUnlockedVisibleEventIds]);
    const unlockedCount = unlockedVisibleIds.length;

    // For outside-click disarm (wraps the delete button)
    const deleteControlsRef = useRef(null);
    const deleteConfirmRef = useRef(null);
    const modalRef = useRef(null);

    useEffect(() => {
        const onDocMouseDown = (e) => {
            if (!showDeleteConfirm) return;
            // If click is inside the delete-controls (toolbar) or inside the modal, ignore it
            if (deleteControlsRef.current?.contains(e.target)) return;
            if (modalRef.current?.contains(e.target)) return;
            disarmDelete();
        };
        document.addEventListener('mousedown', onDocMouseDown);
        return () => document.removeEventListener('mousedown', onDocMouseDown);
    }, [showDeleteConfirm, disarmDelete]);

    // Manage focus + Escape key for the delete confirmation modal
    useEffect(() => {
        if (!showDeleteConfirm) return;

        // Focus the cancel button when modal opens to avoid accidental deletes
        const toFocus = deleteConfirmRef.current;
        toFocus?.focus?.();

        const onKeyDown = (e) => {
            // Close on Escape
            if (e.key === 'Escape') {
                setShowDeleteConfirm(false);
                return;
            }

            // Trap Tab/Shift+Tab inside modal
            if (e.key === 'Tab') {
                const container = modalRef.current;
                if (!container) return;
                const focusable = Array.from(
                    container.querySelectorAll(
                        'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
                    )
                ).filter(el => !el.hasAttribute('disabled') && el.offsetParent !== null);
                if (focusable.length === 0) {
                    e.preventDefault();
                    return;
                }
                const first = focusable[0];
                const last = focusable[focusable.length - 1];
                const active = document.activeElement;
                if (e.shiftKey) {
                    // Shift + Tab
                    if (active === first) {
                        e.preventDefault();
                        last.focus();
                    }
                } else {
                    // Tab
                    if (active === last) {
                        e.preventDefault();
                        first.focus();
                    }
                }
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [showDeleteConfirm]);


    return (
        <main
            ref={scrollContainerRef}
            className="fixed top-16 inset-0 bg-gray-100 overflow-y-auto overflow-x-auto"
        >
            {/* Date-Range Toolbar */}
            <div className="fixed top-20 right-6 z-50 bg-white/90 backdrop-blur-md border border-gray-300 rounded-xl shadow p-3 flex items-center space-x-2">
                <label className="text-sm text-gray-700">Von</label>
                <input
                    type="date"
                    className="border rounded px-2 py-1 text-sm"
                    value={selectedStartDate}
                    onChange={handleStartChange}
                    /* allow the user to increase start past end; handleStartChange will push end forward when needed */
                />
                <label className="text-sm text-gray-700">Bis</label>
                <input
                    type="date"
                    className="border rounded px-2 py-1 text-sm"
                    value={selectedEndDate}
                    onChange={(e) => setSelectedEndDate(e.target.value)}
                    min={selectedStartDate}
                />
                <button
                    onClick={toggleAllLocks}
                    role="switch"
                    aria-checked={allLocked}
                    aria-label={allLocked ? 'Alle Schlösser öffnen' : 'Alle Schlösser schließen'}
                    title={allLocked ? 'Alle Schlösser öffnen (entsperren)' : 'Alle Schlösser schließen (sperren)'}
                    className="relative flex items-center bg-gray-200 rounded-full p-1 w-[4.5rem] shadow-inner
                        focus:outline-none focus:ring-2 focus:ring-indigo-400"
                >
                    {/* sliding highlight to mimic the two-button look */}
                    <span
                        className={`absolute top-1 left-1 w-8 h-8 rounded-full transition-all duration-200
                ${allLocked ? 'translate-x-0 bg-white shadow' : 'translate-x-8 bg-gray-700 shadow-md'}`}
                        aria-hidden="true"
                    />

                    {/* left segment: LOCK (alle schließen) */}
                    <span className="relative z-10 w-8 h-8 flex items-center justify-center rounded-full">
                        <img
                            src={Lock}
                            alt=""
                            className={`w-4 h-4 transition-opacity
                                      ${allLocked ? 'opacity-100' : 'opacity-60'}`}
                            aria-hidden="true"
                        />
                    </span>

                    {/* right segment: UNLOCK (alle öffnen) */}
                    <span className="relative z-10 w-8 h-8 flex items-center justify-center rounded-full">
                        <img
                            src={Unlock}
                            alt=""
                            className={`w-4 h-4 transition-all
                                      ${allLocked ? 'opacity-60' : 'opacity-100 invert'}`}
                            /* black SVG → white on the dark active segment via invert */
                            aria-hidden="true"
                        />
                    </span>
                </button>
                <div ref={deleteControlsRef} className="inline-flex">
                    <button
                        onClick={handleDeleteClick}
                        disabled={isDeleting || unlockedCount === 0}
                        aria-pressed={showDeleteConfirm}
                        aria-label={isDeleting ? 'Lösche…' : unlockedCount === 0 ? 'Keine offenen sichtbaren Events' : 'Offene sichtbare Events löschen'}
                        title={isDeleting ? 'Lösche…' : unlockedCount === 0 ? 'Keine offenen Events' : 'Nicht geschlossene sichtbare Events löschen'}
                        className={[
                            'flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm shadow transition',
                            isDeleting || unlockedCount === 0
                                ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                : showDeleteConfirm
                                    ? 'bg-red-600 text-white border-red-700 hover:bg-red-700'
                                    : 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100'
                        ].join(' ')}
                    >
                        <img src={Trash} alt="" className="w-4 h-4" aria-hidden="true" />
                        {isDeleting && <span>Löschen...</span>}
                    </button>
                </div>
            </div>

            <div className="px-6 py-8">
                <div className="relative">
                    {(() => {
                        const positionedEventMap = {};
                        return fullDayList.map((date, dayIndex) => {
                            const currentEvents = groupedEvents[date] || [];
                            const previousDate = dayIndex > 0 ? fullDayList[dayIndex - 1] : null;
                            const previousDayEvents = previousDate ? positionedEventMap[previousDate] || [] : [];

                            const dayStartY = dayIndex * DAY_HEIGHT;
                            const positionedEvents = positionEvents(currentEvents, previousDayEvents, date);
                            positionedEventMap[date] = positionedEvents;

                            return (
                                <React.Fragment key={date}>
                                    <div
                                        className="absolute left-4 text-gray-600 text-6xl font-bold tracking-wide pointer-events-none select-none opacity-20"
                                        style={{top: `${dayStartY + 20}px`, zIndex: 10}}
                                    >
                                        {dayjs(date).format('dddd, D. MMMM')}
                                    </div>
                                    {
                                        positionedEvents
                                            .filter(event => shouldRenderOnDate(event, date, earliestDate))
                                            .map(event => {
                                                const spanStart = dayjs.max(dayjs(event.timeframe.startTime), earliestDate);
                                                const spanEnd   = dayjs.min(dayjs(event.timeframe.endTime),   latestDate);
                                                const daySpan   = getDaySpan(spanStart, spanEnd);
                                                const left      = event.column * (EVENT_WIDTH + COLUMN_GAP);
                                                const height    = daySpan * DAY_HEIGHT - 20;

                                                // >>> NEU: Überlauf-Flags berechnen
                                                const realStart = dayjs(event.timeframe.startTime).startOf('day');
                                                const realEnd   = dayjs(event.timeframe.endTime).startOf('day');
                                                const showFadeTop    = realStart.isBefore(earliestDate, 'day');
                                                const showFadeBottom = realEnd.isAfter(latestDate, 'day');


                                                return (
                                                    <div
                                                        key={event.id}
                                                        className="absolute"
                                                        style={{
                                                            top: `${dayStartY + 60}px`,
                                                            left: `${left}px`,
                                                            width: `${EVENT_WIDTH}px`,
                                                            height: `${height}px`,
                                                        }}
                                                    >
                                                        <EventCard
                                                            event={event}
                                                            allQualifications={allQualifications}
                                                            onUpdated={() => fetchEventsForRange(selectedStartDate, selectedEndDate)}
                                                            onDeleted={handleEventDeleted}
                                                            onLockChange={handleLockChange}
                                                            lockedMap={lockMapPerEvent[event.id] || {}}
                                                            onEventTimeframeChanged={handleEventTimeframeChanged}
                                                            showFadeTop={showFadeTop}
                                                            showFadeBottom={showFadeBottom}
                                                        />
                                                    </div>
                                                );
                                            })
                                    }
                                </React.Fragment>
                            );
                        });
                    })()}
                </div>
            </div>
            <div className="fixed bottom-6 right-6 flex flex-col items-end space-y-3 z-50">
                <button
                    onClick={() => {
                        setShowAddEventPanel(prev => {
                            if (!prev) setShowMatchPanel(false);
                            return !prev;
                        });
                    }}
                    className="bg-indigo-600 hover:bg-indigo-700 rounded-full shadow-lg w-12 h-12 flex items-center justify-center"
                    title="Manuell Event hinzufügen"
                >
                    <img
                        src={Plus}
                        alt="Add"
                        className={`w-5 h-5 pointer-events-none transition-transform duration-500 ease-in-out transform ${showAddEventPanel ? 'rotate-45' : 'rotate-0'}`}
                    />
                </button>

                <button
                    onClick={() => {
                        setShowMatchPanel(prev => {
                            if (!prev) setShowAddEventPanel(false);
                            return !prev;
                        });
                    }}
                    className={`bg-gray-600 hover:bg-gray-700 rounded-full shadow-lg w-12 h-12 flex items-center justify-center
                          transition-transform duration-500 ease-in-out transform ${showMatchPanel ? 'rotate-45' : 'rotate-0'}`}
                    title={showMatchPanel ? 'Schließen' : 'Planung öffnen'}
                    style={{willChange: 'transform'}}
                >
                    <img src={Calendar} alt="Plan Events" className="w-5 h-5 pointer-events-none"/>
                </button>

                {showMatchPanel && (
                    <div
                        className="fixed bottom-6 right-[84px] bg-white/90 backdrop-blur-md pt-10 pb-4 px-4 rounded-xl shadow-xl border border-gray-300 z-40 w-auto">
                        <button
                            onClick={() => setShowMatchPanel(false)}
                            className="absolute -top-4 right-2 w-8 h-8 rounded-full bg-white shadow-md flex items-center justify-center text-gray-600 hover:text-gray-800"
                            title="Schließen"
                        >
                            ✕
                        </button>
                        <MatchEventsButton
                            lockMapPerEvent={lockMapPerEvent}
                            onSuccess={() => {
                                // Close the match panel and refresh events for the current range
                                setShowMatchPanel(false);
                                void fetchEventsForRange(selectedStartDate, selectedEndDate);
                            }}
                        />
                    </div>
                )}

                {showAddEventPanel && (
                    <div
                        className="fixed bottom-[90px] right-[84px] bg-white/90 backdrop-blur-md px-4 pt-4 pb-6 rounded-xl shadow-xl border border-gray-300 z-40 w-[480px] max-h-[calc(100vh-150px)] overflow-y-auto">
                        <div className="flex justify-between items-center mb-2">
                            <h2 className="text-lg font-semibold text-gray-800">Neues Event</h2>
                            <button
                                onClick={() => setShowAddEventPanel(false)}
                                className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center text-gray-600 hover:text-gray-800 transition"
                                title="Schließen"
                            >
                                ✕
                            </button>
                        </div>

                        <NewEventForm
                            onClose={() => setShowAddEventPanel(false)}
                            onSuccess={() => {
                                setShowAddEventPanel(false);
                                window.location.reload();
                            }}
                        />
                    </div>
                )}
            </div>

            {/* Delete confirmation modal — rendered at top-level so it centers in the viewport */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div className="absolute inset-0 bg-black/50" onClick={() => setShowDeleteConfirm(false)} />
                    <div
                        ref={modalRef}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="delete-modal-title"
                        className="relative bg-white rounded-lg shadow-lg p-6 w-[420px] z-10"
                    >
                        <h3 id="delete-modal-title" className="text-lg font-semibold mb-2">Sichtbare Events löschen</h3>
                        <p>Willst du wirklich die offenen sichtbaren Events löschen? ({unlockedCount})</p>
                        <div className="mt-4 flex justify-end gap-2">
                            <button
                                ref={deleteConfirmRef}
                                onClick={() => setShowDeleteConfirm(false)}
                                className="px-3 py-1 rounded border bg-white hover:bg-gray-50"
                            >
                                Abbrechen
                            </button>
                            <button
                                onClick={async () => {
                                    setShowDeleteConfirm(false);
                                    await performDeleteVisibleEvents();
                                }}
                                disabled={unlockedCount === 0}
                                className={['px-3 py-1 rounded text-white', unlockedCount === 0 ? 'bg-gray-300 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'].join(' ')}
                            >
                                Löschen
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </main>
    );
};

export default Plantafel;

